import { first, isNil } from 'lodash';
import { CSSProperties, useCallback, useMemo, useState } from 'react';
import {
  GeolocationContent,
  PromptBottomSheetContent,
  useFetchCurrentGeolocation,
} from '@shared/components/Geolocation';
import { View } from '@shared/components/View';
import { MBLoadingIndicator } from '@shared/components/loading';
import { GeolocationField } from '@shared/data/entity/fields/GeolocationField';
import { getAnchorName, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { GeolocationSnapshot } from '@shared/types/fieldValues';
import { formatDateTimeWithYear } from '@shared/util/date';
import { useBoolean, useCurrentUserId, useIsExternal, useListUsers } from '@shared/util/hooks';
import { EditFieldContainer } from './EditFieldContainer';

interface Props {
  field: GeolocationField | null;
  component: API.GeolocationComponentFragment;
  updateField: (componentId: string, field: GeolocationField | null) => void;
  error?: string;
  showUserName?: boolean;
  style?: CSSProperties;
}

export function GeolocationEditField({
  field,
  component,
  updateField,
  error,
  showUserName,
  style,
}: Props) {
  const anchorName = getAnchorName('geolocation-edit', component.id);
  const [isModalVisisble, showModal, hideModal] = useBoolean(false);
  const currentUserId = useCurrentUserId();
  const isExternal = useIsExternal();
  const [isFetching, setIsFetching] = useState(false);

  const onClear = useCallback(() => updateField(component.id, null), [updateField, component.id]);
  const fetchCurrentGeolocation = useFetchCurrentGeolocation();
  const handleFetchLocation = useCallback(async () => {
    if (isNil(currentUserId)) {
      return;
    }
    hideModal();
    try {
      setIsFetching(true);
      const coords = await fetchCurrentGeolocation();
      const updatedGeolocation: GeolocationSnapshot = {
        userId: currentUserId,
        date: new Date().toISOString(),
        accuracy: coords.accuracy,
        coords: {
          lat: coords.latitude,
          lon: coords.longitude,
        },
      };
      updateField(component.id, new GeolocationField(updatedGeolocation));
    } catch (err) {
      console.error(err);
    } finally {
      setIsFetching(false);
    }
  }, [fetchCurrentGeolocation, currentUserId, updateField, component.id, hideModal]);

  const geolocation = field?.geolocation;
  const hasValue = !isNil(geolocation) && !isFetching;
  const isReadOnly = component.readonly ?? false;
  const shouldShowModalOnPress = !isReadOnly && !hasValue;
  const showClearButton = hasValue && !isReadOnly;
  const { users } = useListUsers({
    input: { ids: geolocation ? [geolocation.userId] : [], excludeSystemUsers: false },
    skip: isExternal || isNil(geolocation),
  });

  const geolocationUser = useMemo(() => first(users), [users]);
  const printContent = geolocation
    ? [
        `${geolocation.coords.lat}, ${geolocation.coords.lon}`,
        geolocationUser?.fullName,
        formatDateTimeWithYear(geolocation.date),
      ]
        .filter(Boolean)
        .join('\n') || undefined
    : undefined;

  return (
    <EditFieldContainer
      onPress={shouldShowModalOnPress ? showModal : undefined}
      component={component}
      anchorName={anchorName}
      showIcon={!hasValue}
      showPlaceholder={!hasValue && !isFetching}
      showClearButton={showClearButton}
      onClear={onClear}
      error={error}
      largerInput={!hasValue}
      style={style}
      printContent={printContent}
    >
      {isNil(geolocation) && isFetching && (
        <View row centerVertically style={{ height: grid(4), justifyContent: 'flex-start' }}>
          <MBLoadingIndicator />
        </View>
      )}
      {!isNil(geolocation) && !isFetching && (
        <GeolocationContent geolocation={geolocation} showUserName={showUserName} />
      )}
      {isModalVisisble && (
        <PromptBottomSheetContent
          anchorName={anchorName}
          onPress={handleFetchLocation}
          onClose={hideModal}
        />
      )}
    </EditFieldContainer>
  );
}
